import { useIntl } from "react-intl";
import { DataTableBooleanColumn } from "~components/DataTable/components/DataTableBooleanColumn/DataTableBooleanColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { FixtureRosterTableColumns } from "~pages/Roster/FixtureRosterListPage/enums/fixtureRosterTableColumns.ts";
import type { FixtureRosterDataTableRow } from "~pages/Roster/FixtureRosterListPage/types/fixtureRosterDataTableRow.ts";
import type { UpsertFixtureRosterModel } from "~pages/Roster/FixtureRosterListPage/types/upsertFixtureRosterModel";
import { useSharedRosterCapsColumn } from "~pages/Roster/hooks/useSharedRosterCapsColumn.tsx";
import { useSharedRosterCaptainColumn } from "~pages/Roster/hooks/useSharedRosterCaptainColumn.tsx";
import { useSharedRosterPersonColumns } from "~pages/Roster/hooks/useSharedRosterPersonColumns.tsx";

type Props = {
  massEditMode: boolean;
  isRosterEditable: boolean;
  isAddToStarterAllowed: boolean;
  handleUpdateField: (
    row: FixtureRosterDataTableRow,
    update: Partial<Pick<UpsertFixtureRosterModel, "bib" | "position" | "isCaptain" | "starter">>,
  ) => Promise<void>;
  onCaptainToggle: (row: FixtureRosterDataTableRow, isCaptain: boolean) => Promise<void>;
};

type RosterRow = FixtureRosterDataTableRow & DataTableRow;

export const useGetFixtureRosterDataTableColumns = ({
  massEditMode,
  isRosterEditable,
  isAddToStarterAllowed,
  handleUpdateField,
  onCaptainToggle,
}: Props): Array<DataTableColumn<FixtureRosterDataTableRow>> => {
  const { formatMessage } = useIntl();

  const sharedPersonColumns = useSharedRosterPersonColumns<FixtureRosterDataTableRow>({
    massEditMode,
    handleUpdateField,
  });
  const captainColumn = useSharedRosterCaptainColumn<RosterRow>({
    massEditMode,
    isRosterEditable,
    onCaptainToggle,
  });
  const capsColumn = useSharedRosterCapsColumn<FixtureRosterDataTableRow>();

  const starterColumn: DataTableColumn<FixtureRosterDataTableRow> = {
    key: FixtureRosterTableColumns.Starter,
    title: formatMessage({ id: "roster.starter" }),
    dataIndex: "starter",
    width: 90,
    render: (value: boolean, row: RosterRow) => (
      <DataTableBooleanColumn
        value={value}
        isEditMode={massEditMode}
        disabled={row.disabled || !isRosterEditable || (!isAddToStarterAllowed && !value)}
        onSave={(starter) => handleUpdateField(row, { starter })}
      />
    ),
  };

  return [...sharedPersonColumns, starterColumn, captainColumn, capsColumn];
};
